import { useEventAlarm } from "@/hooks/useEventAlarm";
import { useEventModal } from "@/hooks/useEventModal";

const SEV = {
  CRITICAL: { label: "위험", bar: "#FB2C36", bg: "#FEF2F2", text: "#C10007" },
  WARNING: { label: "경고", bar: "#FF6900", bg: "#FFF7ED", text: "#CA3500" },
} as const;

/**
 * 실시간 이벤트 알람 배너.
 *
 * `App` 이 라우트 밖에서 그리므로 **어느 화면 위에도** 뜬다. 그래서 위치를 공용
 * 헤더(`SiteShellHeader`, 3.75rem) 아래로 잡는다 — `top-0` 이면 헤더를 통째로 덮어
 * 로고·현장명·알람 벨·사용자가 배너가 떠 있는 동안 눌리지 않는다. 배너 z(100)가
 * 헤더 z(30)보다 높아 화면 폭이 좁을수록(모바일) 헤더 전체가 가려진다.
 *
 * 헤더가 없는 화면(`/health`·`/login`)에서는 그만큼 아래에서 시작한다 — 가림을
 * 막는 쪽이 우선이다.
 */
export function EventAlarmBanner() {
  const { openEvent } = useEventModal();
  const { alarm, site, dismiss } = useEventAlarm();
  if (!alarm) return null;

  const sev = alarm.severity === "CRITICAL" ? SEV.CRITICAL : SEV.WARNING;

  return (
    <div className="fixed inset-x-0 top-[3.75rem] z-[100] flex justify-center px-3 pt-3">
      <div
        role="alert"
        onClick={() => {
          /*
            상세 모달을 연다. 예전에는 현장·CCTV 로 이동했는데, **이미 그 화면을 보고 있을 때**
            (예: `/cctv-ai/17` 에서 17번 현장 알람) 같은 자리로 이동해 아무 일도 일어나지
            않았다. 모달은 어디서 눌러도 내용을 보여준다.
          */
          openEvent(alarm.id);
          dismiss();
        }}
        className="flex w-full max-w-2xl cursor-pointer items-center gap-3 rounded-xl border-l-4 px-4 py-3 shadow-lg"
        style={{ background: sev.bg, borderColor: sev.bar }}
      >
        <span
          className="flex-none rounded-md px-2 py-0.5 text-xs font-bold text-white"
          style={{ background: sev.bar }}
        >
          {sev.label}
        </span>
        <div className="min-w-0 flex-1">
          <div className="truncate text-sm font-semibold" style={{ color: sev.text }}>
            {site ? `[${site.name}] ` : ""}
            {alarm.name}
          </div>
          <div className="truncate text-xs text-neutral-500">
            {site ? "눌러서 상세 확인" : "실시간 이벤트가 감지되었습니다 · 눌러서 확인"}
          </div>
        </div>
        <button
          type="button"
          aria-label="알람 닫기"
          onClick={(e) => {
            e.stopPropagation();
            dismiss();
          }}
          className="flex-none px-1 text-lg leading-none text-neutral-400"
        >
          ✕
        </button>
      </div>
    </div>
  );
}
